import type { WillieSupabaseClient } from '@willie/supabase'
import { OPS_BUCKET, signOpsPath } from '@willie/supabase/storage'
import { todayIso } from '~/utils/date'
import { randomHex } from '~/utils/random'
import type { TRPCOutputs } from '~/utils/trpc'

export type MaterialReturn = TRPCOutputs['ops']['materialReturns']['list'][number]

export const LABEL_MAX_BYTES = 10 * 1024 * 1024

export const isReturnInProgress = (materialReturn: Pick<MaterialReturn, 'received_at'>): boolean =>
  materialReturn.received_at === null

export const isLabelExpired = (
  materialReturn: Pick<MaterialReturn, 'received_at' | 'label_expires_on'>
): boolean => isReturnInProgress(materialReturn) && materialReturn.label_expires_on < todayIso()

export const dhlTrackingUrl = (trackingNumber: string): string =>
  `https://www.dhl.com/fr-fr/home/suivi.html?tracking-id=${encodeURIComponent(trackingNumber)}`

export const formatRecipients = (materialReturn: Pick<MaterialReturn, 'contact' | 'cc_email'>): string =>
  [materialReturn.contact?.name, materialReturn.cc_email].filter(Boolean).join(', cc ') || 'unknown contact'

export const uploadReturnLabel = async (supabaseClient: WillieSupabaseClient, file: File): Promise<string> => {
  const path = `returns/${randomHex(16)}/label.pdf`
  const { error } = await supabaseClient.storage
    .from(OPS_BUCKET)
    .upload(path, file, { contentType: 'application/pdf', upsert: false })
  if (error) throw error
  return path
}

export const removeReturnLabel = async (supabaseClient: WillieSupabaseClient, path: string): Promise<void> => {
  await supabaseClient.storage.from(OPS_BUCKET).remove([path])
}

// The tab is opened before signing: a window.open after an await is no longer tied to the click,
// and popup blockers drop it.
export const openReturnLabel = async (supabaseClient: WillieSupabaseClient, path: string): Promise<void> => {
  const tab = window.open('', '_blank')
  try {
    const url = await signOpsPath(supabaseClient, path, 'display')
    if (tab) {
      tab.opener = null
      tab.location.href = url
    }
  } catch (error) {
    tab?.close()
    throw error
  }
}
